import { defineStore } from "pinia";
import { useQuery } from "@vue/apollo-composable";
import { UPDATE_FAVORITES } from "~/graphql/Favorites";
import { GET_USER_FAVORITES } from "~/graphql/GetFavorites";
import { useBcnAuth } from "./bcnAuth";

export const useFavorites = defineStore("favorites", () => {
  const bcnAuth = useBcnAuth();
  const { isLoggedIn, userCookie, tokenCookie } = storeToRefs(bcnAuth);

  const notificationIsOpen = ref(false);
  const notificationProductName = ref("");
  const notificationImageUrl = ref("");
  const notificationCountdownProgress = ref(100);
  const notificationCurrentInterval = ref(null);
  const notificationType = ref("success"); // 'success' | 'error'
  const notificationMessage = ref("");
  const refreshing = ref(false);

  const queue = ref([]);
  const processingQueue = ref(false);
  const debounceDelay = 2000;
  let debounceTimer = null;

  // Configuration for retry logic
  const MAX_RETRY_ATTEMPTS = 3;
  const RETRY_DELAY_MS = 1000; // Initial delay, will use exponential backoff

  /**
   * Enqueues a request with a specific action and ID into the queue.
   * The request is processed if the queue is not already being processed.
   *
   * @param {Object} request - The request object to be enqueued.
   * @param {'add'|'remove'} request.action - The action type of the request. Must be either 'add' or 'remove'.
   * @param {number} request.id - The ID associated with the request. Must be an integer.
   */
  const enqueueRequest = (request) => {
    queue.value.push(request);

    if (!processingQueue.value) {
      processQueue();
    }
  };

  const processQueue = async () => {
    if (processingQueue.value) return;

    if (queue.value.length === 0) {
      // If the queue is empty, reset the processing state and do nothing.
      processingQueue.value = false;
      return;
    }

    processingQueue.value = true;

    // Process the first request immediately if it's the only item in the queue
    // to increase perceived resposiveness
    if (queue.value.length === 1) {
      await processRequests();
    } else {
      // Debounce subsequent requests
      debounceSubsequentRequests();
    }
  };

  const processRequests = async () => {
    // Store the original favorites state before any changes
    const originalFavorites = userCookie.value?.favorites
      ? JSON.parse(JSON.stringify(userCookie.value.favorites))
      : "[]";

    try {
      // Batch processing: Process all requests in the queue at once
      const requestsToProcess = [...queue.value];
      queue.value = []; // Clear the queue

      const userId = userCookie?.value?.userId ?? -1;

      // Validate user is logged in before processing
      if (!isLoggedIn.value || !userId || userId === -1) {
        throw new Error("User must be logged in to manage favorites");
      }

      // Validate auth token exists
      if (!tokenCookie.value) {
        throw new Error("Authentication token expired. Please log in again.");
      }

      let cookieFavorites = [];
      if (isValidJSON(userCookie.value.favorites)) {
        const parsedFavorites = JSON.parse(userCookie.value.favorites);
        if (Array.isArray(parsedFavorites)) {
          cookieFavorites = parsedFavorites.filter(
            (item) => typeof item === "number",
          );
        }
      }
      let localFavorites = new Set(cookieFavorites);

      requestsToProcess.forEach((request) => {
        if (request.action === "add") {
          localFavorites.add(request.id);
        }
        if (request.action === "remove") {
          localFavorites.delete(request.id);
        }
      });

      const newFavorites = JSON.stringify([...localFavorites]);

      // Optimistically update the UI AFTER validation but before server call
      userCookie.value.favorites = newFavorites;

      // Attempt to save to server with retry logic
      const serverFavorites = await updateFavoritesWithRetry(userId, newFavorites, originalFavorites);

      // Update cookie with the server's authoritative response
      userCookie.value.favorites = serverFavorites;

    } catch (error) {
      console.error("Failed to process favorites after retries:", error);

      // Revert to original state on failure
      userCookie.value.favorites = originalFavorites;

      // Show error notification
      showErrorNotification(error.message || "Failed to save favorite. Please try again.");

      // Optionally refresh login status if auth error
      if (error.message?.includes("Authentication") || error.message?.includes("logged in")) {
        await bcnAuth.refreshLoginStatus();
      }
    } finally {
      processingQueue.value = false;
      if (queue.value.length > 0) {
        processQueue(); // Process new items added during processing
      }
    }
  };

  /**
   * Updates favorites with automatic retry logic
   * @param {number} userId - User ID
   * @param {string} newFavorites - JSON string of favorites array
   * @param {string} originalFavorites - Original favorites for rollback
   * @param {number} attemptNumber - Current attempt number (for recursion)
   * @returns {Promise<string>} Server's authoritative favorites state
   */
  const updateFavoritesWithRetry = async (userId, newFavorites, originalFavorites, attemptNumber = 1) => {
    try {
      return await updateFavorites(userId, newFavorites);
    } catch (error) {
      console.error(`Favorite save attempt ${attemptNumber} failed:`, error);

      // If we haven't exceeded max retries, try again with exponential backoff
      if (attemptNumber < MAX_RETRY_ATTEMPTS) {
        const delayMs = RETRY_DELAY_MS * Math.pow(2, attemptNumber - 1);
        console.log(`Retrying in ${delayMs}ms... (attempt ${attemptNumber + 1}/${MAX_RETRY_ATTEMPTS})`);

        await new Promise(resolve => setTimeout(resolve, delayMs));

        return await updateFavoritesWithRetry(userId, newFavorites, originalFavorites, attemptNumber + 1);
      }

      // All retries exhausted, throw the error
      throw error;
    }
  };

  const debounceSubsequentRequests = () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      processRequests();
    }, debounceDelay);
  };

  const refreshAll = async () => {
    refreshing.value = true;
    try {
      await refreshNuxtData();
    } finally {
      refreshing.value = false;
    }
  };

  const notificationStartCountdown = () => {
    if (notificationCurrentInterval.value) {
      clearInterval(notificationCurrentInterval.value);
    }

    const intervalTime = 50;
    const decrement = (intervalTime / 8000) * 100;
    notificationCurrentInterval.value = setInterval(() => {
      notificationCountdownProgress.value -= decrement;
      if (notificationCountdownProgress.value <= 0) {
        notificationCountdownProgress.value = 0;
        notificationIsOpen.value = false;
        clearInterval(notificationCurrentInterval.value);
      }
    }, intervalTime);
  };

  const openNotification = () => {
    notificationIsOpen.value = true;
  };

  const closeNotification = () => {
    notificationIsOpen.value = false;
    if (notificationCurrentInterval.value) {
      clearInterval(notificationCurrentInterval.value);
    }
  };

  const showErrorNotification = (message) => {
    notificationType.value = "error";
    notificationMessage.value = message;
    notificationProductName.value = "";
    notificationImageUrl.value = "";
    notificationCountdownProgress.value = 100;
    openNotification();
    notificationStartCountdown();
  };

  const showSuccessNotification = (product) => {
    notificationType.value = "success";
    notificationMessage.value = "";
    notificationProductName.value = product.title;
    notificationImageUrl.value = product.thumbnail;
    notificationCountdownProgress.value = 100;
    openNotification();
    notificationStartCountdown();
  };

  const toggleFavoriteProduct = (product) => {
    const userId = userCookie?.value?.userId ?? -1;

    const cookieFavorites = isValidJSON(userCookie.value.favorites)
      ? JSON.parse(userCookie.value.favorites)
      : [];

    const productIsFavorite = isFavorite(product.id, cookieFavorites);

    if (productIsFavorite) {
      enqueueRequest({ action: "remove", id: product.id });
      return;
    } else {
      enqueueRequest({ action: "add", id: product.id });
      showSuccessNotification(product);
      return;
    }
  };

  const isFavorite = (id, cookieFavorites) => {
    return cookieFavorites && Array.isArray(cookieFavorites)
      ? cookieFavorites?.some((itemId) => {
          return itemId === id;
        })
      : false;
  };

  /**
   * Fetches current favorites from server to ensure sync
   * Should be called periodically or after critical operations
   */
  const syncFavoritesFromServer = async () => {
    try {
      const userId = userCookie?.value?.userId;
      if (!userId || !isLoggedIn.value) {
        return;
      }

      // Fetch user data from server (you'll need to implement this GraphQL query)
      const { data } = await useAsyncQuery(GET_USER_FAVORITES, {
        userId: userId,
      });

      if (data.value?.user?.favorites) {
        const serverFavorites = data.value.user.favorites;

        // Only update if different from current state
        if (userCookie.value.favorites !== serverFavorites) {
          console.log("Syncing favorites from server - detected mismatch");
          userCookie.value.favorites = serverFavorites;
        }
      }
    } catch (error) {
      console.error("Failed to sync favorites from server:", error);
    }
  };

  const {
    mutate: favoritesMutate,
    loading: favoritesSubmitting,
    error: favoritesError,
    called: favoritesCalled,
    onDone: favoritesOnDone,
    onError: favoritesOnError,
  } = useMutation(UPDATE_FAVORITES);

  const updateFavorites = (userId, newFavorites) => {
    return new Promise((resolve, reject) => {
      // Validate BEFORE making any changes
      if (!userId || userId === -1) {
        reject(new Error("Invalid user ID"));
        return;
      }

      if (!newFavorites) {
        reject(new Error("Invalid favorites data"));
        return;
      }

      if (isLoggedIn.value !== true) {
        reject(new Error("User must be logged in"));
        return;
      }

      if (!tokenCookie.value) {
        reject(new Error("Authentication token expired"));
        return;
      }

      // Validate JSON format
      try {
        const parsed = JSON.parse(newFavorites);
        if (!Array.isArray(parsed)) {
          reject(new Error("Favorites must be an array"));
          return;
        }
      } catch (e) {
        reject(new Error("Invalid favorites JSON format"));
        return;
      }

      favoritesMutate({
        uniqueId: generateRandomString(8),
        userId: userId,
        favorites: newFavorites,
      });

      favoritesOnDone((result) => {
        const resultFavorites = isValidJSON(
          result?.data?.updateUserFavorites?.user?.favorites,
        )
          ? result?.data?.updateUserFavorites?.user?.favorites
          : JSON.stringify([]);

        // Server response is the source of truth
        resolve(resultFavorites);
      });

      favoritesOnError((error) => {
        console.error("GraphQL mutation error:", error);

        // Parse error message for better user feedback
        let errorMessage = "Failed to save favorite";
        if (error.message?.includes("edit_user")) {
          errorMessage = "Permission denied. Please log in again.";
        } else if (error.message?.includes("JWT")) {
          errorMessage = "Session expired. Please log in again.";
        } else if (error.networkError) {
          errorMessage = "Network error. Please check your connection.";
        }

        reject(new Error(errorMessage));
      });
    });
  };

  return {
    closeNotification,
    enqueueRequest,
    favoritesSubmitting,
    isFavorite,
    notificationCountdownProgress,
    notificationCurrentInterval,
    notificationImageUrl,
    notificationIsOpen,
    notificationMessage,
    notificationProductName,
    notificationStartCountdown,
    notificationType,
    openNotification,
    processingQueue,
    syncFavoritesFromServer,
    toggleFavoriteProduct,
  };
});
